import React, { useState, useRef, useEffect } from 'react';
import TermsAndConditionsService from '../../modules/Admin/services/TermsAndConditionsService';
import ToastService from '../../modules/Admin/services/ToastService';
import { Toast } from 'primereact/toast';
import AuthService from '../../modules/Admin/services/AuthService';

const C = {
  ink: '#344767', muted: '#8392ab', border: '#e9ecef',
  primary: '#5e72e4', success: '#2dce89',
  bgSoft: '#f8f9fe', shadow: '0 2px 24px rgba(52,71,103,0.09)',
};

const SECTIONS = [
  {
    num: '01', icon: 'fas fa-handshake', color: '#5e72e4', title: 'Acceptance of Terms',
    body: 'By accessing or using Next Follow Up, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must discontinue use of the platform immediately.',
  },
  {
    num: '02', icon: 'fas fa-server', color: '#fb6340', title: 'Platform Description',
    body: 'Next Follow Up is a cloud-based Real Estate CRM that helps agencies capture, manage, and convert leads. Features include lead pipeline management, follow-up reminders, multi-channel integrations, team management, and reporting.',
  },
  {
    num: '03', icon: 'fas fa-user-lock', color: '#2dce89', title: 'Account Responsibilities',
    body: 'You are responsible for maintaining the confidentiality of your login credentials. Any activity that occurs under your account is your responsibility. Please notify us immediately if you suspect unauthorised access.',
  },
  {
    num: '04', icon: 'fas fa-ban', color: '#f5365c', title: 'Prohibited Use',
    body: 'You may not use the platform to distribute spam, engage in fraudulent activity, attempt to breach system security, upload malicious code, or violate any applicable laws. Violations may result in immediate account suspension.',
  },
  {
    num: '05', icon: 'fas fa-database', color: '#11cdef', title: 'Data & Privacy',
    body: "Your use of the platform is also governed by our Privacy Policy. By using Next Follow Up, you consent to the collection and processing of data as described therein. We are committed to GDPR and India's IT Act compliance.",
  },
  {
    num: '06', icon: 'fas fa-copyright', color: '#825ee4', title: 'Intellectual Property',
    body: 'All software, design, trademarks, and content on Next Follow Up are the exclusive property of the platform owner. Unauthorised reproduction, distribution, or reverse engineering of any part of the platform is strictly prohibited.',
  },
  {
    num: '07', icon: 'fas fa-exclamation-triangle', color: '#ffd600', title: 'Limitation of Liability',
    body: 'Next Follow Up shall not be held liable for any indirect, incidental, or consequential damages arising from platform use, data loss, or service interruptions. Our maximum liability is limited to fees paid in the preceding 30 days.',
  },
  {
    num: '08', icon: 'fas fa-power-off', color: '#fb6340', title: 'Termination',
    body: 'We reserve the right to suspend or terminate accounts that violate these terms, without prior notice. Upon termination, you may request a data export for 30 days; after that, data may be permanently deleted.',
  },
  {
    num: '09', icon: 'fas fa-balance-scale', color: '#344767', title: 'Governing Law',
    body: 'These Terms are governed by the laws of India. Any disputes arising from the use of Next Follow Up shall be subject to the exclusive jurisdiction of the courts of Chandigarh, Haryana, India.',
  },
  {
    num: '10', icon: 'fas fa-sync-alt', color: '#2dce89', title: 'Amendments',
    body: 'We may update these Terms at any time. Users will be notified of material changes via email or in-app notification. Continued use of the platform after any change constitutes acceptance of the revised Terms.',
  },
];

export default function TermsAndConditions() {
  const [Terms, setTerms] = useState([]);
  const [termId, setTermId] = useState(null);
  const [form, setForm] = useState({ termsandconditionstext: '', createddate: '' });
  const [showForm, setShowForm] = useState(false);
  const toast = useRef(null);
  const toastService = new ToastService(toast);
  const userRole = AuthService.getLoggedInRoleId();
  const rid = parseInt(userRole, 10);
  const canEdit = rid === 1 || rid === 2 || rid === 3;

  useEffect(() => {
    TermsAndConditionsService.getAllTerms().then(setTerms).catch(() => {});
  }, []);

  const resetForm = () => { setForm({ termsandconditionstext: '', createddate: '' }); setTermId(null); setShowForm(false); };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (termId) {
        const fd = new FormData();
        fd.append('termsandconditionstext', form.termsandconditionstext);
        fd.append('createddate', form.createddate);
        fd.append('id', termId);
        await TermsAndConditionsService.updateTerms(termId, fd);
        toastService.showSuccess('Saved', 'Terms updated.');
      } else {
        await TermsAndConditionsService.createTerms({ termsandconditionstext: form.termsandconditionstext, createddate: form.createddate });
        toastService.showSuccess('Saved', 'Terms entry added.');
      }
      resetForm();
      const updated = await TermsAndConditionsService.getAllTerms();
      setTerms(updated);
    } catch { toastService.showError('Error', 'Failed to save.'); }
  };

  return (
    <div style={{ background: C.bgSoft, minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* hero */}
      <div style={{ background: 'linear-gradient(135deg,#344767 0%,#21243d 100%)', padding: '52px 24px 68px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', width: 64, height: 64, borderRadius: 18, background: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: '#fff', marginBottom: 18 }}>
          <i className="fas fa-file-contract" />
        </div>
        <h1 style={{ margin: '0 0 10px', fontSize: 32, fontWeight: 800, color: '#fff' }}>Terms & Conditions</h1>
        <p style={{ margin: '0 auto', maxWidth: 500, fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
          Please read these terms carefully before using the Next Follow Up platform.
        </p>
        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Effective date: May 2026</p>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 20px 60px' }}>

        {/* intro */}
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: C.shadow, padding: '28px 32px', marginTop: -28, marginBottom: 28 }}>
          <p style={{ margin: 0, fontSize: 15, color: C.muted, lineHeight: 1.7 }}>
            These Terms and Conditions constitute a legally binding agreement between you and Next Follow Up. By creating an account or using any feature of the platform, you acknowledge that you have read, understood, and agree to these terms.
          </p>
        </div>

        {/* sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
          {SECTIONS.map(({ num, icon, color, title, body }) => (
            <div key={num} style={{ background: '#fff', borderRadius: 16, boxShadow: C.shadow, padding: '22px 26px', display: 'flex', gap: 18, alignItems: 'flex-start' }}>
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color }}>
                  <i className={icon} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: C.muted, letterSpacing: '0.04em' }}>{num}</span>
              </div>
              <div>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 800, color: C.ink }}>{title}</h3>
                <p style={{ margin: 0, fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* contact CTA */}
        <div style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)', borderRadius: 18, padding: '28px 32px', textAlign: 'center', marginBottom: 32 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#fff' }}>Questions about these terms?</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>Contact our team and we'll be happy to clarify.</p>
          <a href="mailto:support@nextfollowup.in"
            style={{ background: '#fff', color: C.primary, borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-envelope" /> support@nextfollowup.in
          </a>
        </div>

        {/* admin: dynamic terms entries */}
        {canEdit && (
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: C.shadow, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg,#344767,#21243d)', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fas fa-edit" style={{ color: '#fff', fontSize: 15 }} />
                <span style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>Manage Custom Entries</span>
              </div>
              <button onClick={() => { setShowForm(!showForm); resetForm(); }}
                style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '6px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={`fas fa-${showForm ? 'times' : 'plus'}`} /> {showForm ? 'Cancel' : 'Add Entry'}
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {(showForm || termId) && (
                <form onSubmit={onSubmit} style={{ background: C.bgSoft, borderRadius: 12, padding: '20px', marginBottom: 20, border: `1px solid ${C.border}` }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: C.ink }}>{termId ? 'Edit Entry' : 'New Entry'}</h4>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Terms Text</label>
                    <textarea rows={4} name="termsandconditionstext" value={form.termsandconditionstext}
                      onChange={(e) => setForm(p => ({ ...p, termsandconditionstext: e.target.value }))} required
                      style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 14, color: C.ink, boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit"
                      style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)', border: 'none', borderRadius: 9, padding: '9px 20px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-save" /> {termId ? 'Update' : 'Save'}
                    </button>
                    <button type="button" onClick={resetForm}
                      style={{ background: '#fff', border: `1.5px solid ${C.border}`, borderRadius: 9, padding: '9px 18px', color: C.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {Terms.length === 0 && <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>No custom entries yet.</p>}
              {Terms.map((t) => (
                <div key={t.id} style={{ borderBottom: `1px solid ${C.border}`, padding: '14px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 4px', fontSize: 14, color: C.ink, lineHeight: 1.6 }}>{t.termsAndConditionsText}</p>
                    <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{new Date(t.createdDate).toLocaleString()}</p>
                  </div>
                  <button onClick={() => { setForm({ termsandconditionstext: t.termsAndConditionsText, createddate: t.createdDate }); setTermId(t.id); setShowForm(false); }}
                    style={{ background: `${C.primary}12`, border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.primary, flexShrink: 0 }}>
                    <i className="fas fa-pen" style={{ fontSize: 12 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
